import { AgendaAgendaOpcao, AgendaGradeParams } from '../../../interfaces/agenda';

/** Odontologia só é oferecida na SiM (a Super não tem agenda de odonto). */
export type AgendaTipoFiltro = 'CONSULTA' | 'EXAME' | 'INTEGRADO' | 'ODONTOLOGIA';

export interface AgendaFiltroEstado {
  tipo: AgendaTipoFiltro | null;
  cdEstabelecimento: number | null;
  cdEspecialidade: number | null;
  cdAgenda: number | null;
  /** Só no Integrado: médico dono das agendas. */
  cdMedico: number | null;
  ieClassifAgenda: string | null;
  /** yyyy-MM-dd. */
  data: string;
  cancelados: boolean;
  soAgendados: boolean;
  soLivres: boolean;
}

/** O filtro que a grade (e a visão mensal) consome. Novo objeto a cada aplicação = recarregar. */
export interface AgendaFiltroAplicado extends AgendaFiltroEstado {
  tipo: AgendaTipoFiltro;
  cdEstabelecimento: number;
  /** A agenda escolhida, com a observação e o médico (nulo no Integrado ou sem agenda). */
  agenda: AgendaAgendaOpcao | null;
  /** true = veio de troca de filtro sem clique; a grade não pisca "Carregando". */
  automatico: boolean;
}

/** Filtro com que a Agenda abre no dashboard (ex.: a linha clicada em Agendas do Dia). */
export interface AgendaFiltroEntrada {
  tipo?: AgendaTipoFiltro | null;
  cdEstabelecimento?: number | null;
  cdEspecialidade?: number | null;
  cdAgenda?: number | null;
  /** Rótulo da agenda se ela não vier na lista carregada. */
  nmAgenda?: string | null;
  /** yyyy-MM-dd; inválida ou ausente = hoje. */
  data?: string | null;
}

export type AgendaVisao = 'dia' | 'mes' | 'agendas';

/** Aberta numa agenda específica vai direto à grade; senão começa pelas Agendas do Dia. */
export function visaoDeAbertura(e: AgendaFiltroEntrada | null | undefined): AgendaVisao {
  return e?.cdAgenda ? 'dia' : 'agendas';
}

export function filtroInicial(data: string): AgendaFiltroEstado {
  return {
    tipo: 'CONSULTA', cdEstabelecimento: null, cdEspecialidade: null, cdAgenda: null, cdMedico: null,
    ieClassifAgenda: null, data, cancelados: false, soAgendados: false, soLivres: false,
  };
}

/** Código de tipo de agenda dos filtros do Tasy: consulta e odonto 3, exame 2, Integrado 0 (qualquer). */
export function codigoTipoAgenda(tipo: AgendaTipoFiltro | null): number {
  return tipo === 'CONSULTA' || tipo === 'ODONTOLOGIA' ? 3 : tipo === 'EXAME' ? 2 : 0;
}

/**
 * Odonto é agenda de consulta com NR_SEQ_AREA_ATUACAO 1/2: 1 pede só elas; ausente (0 no back) a
 * consulta as exclui (regra das SQLs de filtro, como no sim-relatorios).
 */
export function areaAtuacaoDoTipo(tipo: AgendaTipoFiltro | null): number | null {
  return tipo === 'ODONTOLOGIA' ? 1 : null;
}

export function tenantTemOdonto(tenant: string | null | undefined): boolean {
  return (tenant ?? '').trim().toUpperCase() === 'SIM';
}

/** Consulta e odonto têm especialidade; exame e Integrado não. */
export function tipoComEspecialidade(tipo: AgendaTipoFiltro | null): boolean {
  return tipo === 'CONSULTA' || tipo === 'ODONTOLOGIA';
}

/**
 * Libera o Filtrar: tipo + estabelecimento + (especialidade, agenda ou tipo Exame). No Integrado
 * o médico faz o papel da agenda; sem ele a grade traria a unidade inteira e o Tasy geraria
 * horário para todas as agendas ativas dela.
 */
export function filtroPronto(f: AgendaFiltroEstado): boolean {
  if (!f.tipo || !f.cdEstabelecimento) return false;
  if (f.tipo === 'INTEGRADO') return !!f.cdMedico;
  return f.tipo === 'EXAME' || !!f.cdEspecialidade || !!f.cdAgenda;
}

/**
 * Carga automática: mais restrita que o Filtrar de propósito. Especialidade sem agenda varre
 * todas as agendas dela (a consulta mais lenta da tela) e só roda com clique.
 */
export function autoCargaPronta(f: AgendaFiltroEstado): boolean {
  if (!filtroPronto(f)) return false;
  return f.tipo === 'INTEGRADO' ? !!f.cdMedico : !!f.cdAgenda;
}

/** Parâmetros de GET grade para o filtro aplicado (página e ordenação ficam com a grade). */
export function paramsGrade(f: AgendaFiltroEstado): AgendaGradeParams {
  const integrado = f.tipo === 'INTEGRADO';
  return {
    data: f.data,
    cdEstabelecimento: f.cdEstabelecimento,
    cdEspecialidade: integrado ? null : f.cdEspecialidade,
    cdAgenda: integrado ? null : f.cdAgenda,
    // Médico DONO da agenda: o médico anotado no agendamento vem vazio em parte dos livres de exame.
    cdMedicoAgenda: integrado ? f.cdMedico : null,
    ieClassifAgenda: f.ieClassifAgenda,
    tipo: f.tipo,
    showCancelled: f.cancelados ? 1 : 0,
    showOnlyScheduled: f.soAgendados ? 1 : 0,
    showOnlyFree: f.soLivres ? 1 : 0,
  };
}

/** Dica do estado vazio, acompanhando o passo da cascata em que o operador está. */
export function dicaFiltro(f: AgendaFiltroEstado): string {
  if (!f.tipo) return 'Escolha o tipo de agenda para começar.';
  if (!f.cdEstabelecimento) return 'Agora escolha a unidade.';
  if (f.tipo === 'INTEGRADO') {
    return filtroPronto(f) ? 'Clique em Filtrar para ver a consulta e o exame deste médico.' : 'Agora escolha o médico.';
  }
  if (filtroPronto(f)) {
    const alvo = f.tipo === 'EXAME' ? 'todas as agendas de exame da unidade' : 'a especialidade inteira';
    return `Escolha a agenda, ou clique em Filtrar para ver ${alvo}.`;
  }
  return 'Agora escolha a especialidade e a agenda.';
}

/** Data local em yyyy-MM-dd (toISOString daria o dia em UTC). */
export function dataLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function somarDias(ds: string, n: number): string {
  const [a, m, d] = ds.split('-').map(Number);
  return dataLocal(new Date(a, m - 1, d + n));
}

/** Mesma agenda/médico/unidade: só mudou data, classificação ou as caixas. */
export function mesmaSelecao(a: AgendaFiltroEstado, b: AgendaFiltroEstado): boolean {
  return a.tipo === b.tipo
    && a.cdEstabelecimento === b.cdEstabelecimento
    && (a.cdEspecialidade ?? null) === (b.cdEspecialidade ?? null)
    && (a.cdAgenda ?? null) === (b.cdAgenda ?? null)
    && (a.cdMedico ?? null) === (b.cdMedico ?? null);
}
